import type { FC } from 'react'
import { useEffect, useState } from 'react'

import { Input } from '@skytech/ui'
import { useDebounce } from '@skytech/utils'

import { usePolicyId } from '@/components/ecom-policy/hooks/use-policy-id'
import type { Policy, ProductGroupData } from '@/components/ecom-policy/types'
import { useTranslation } from '@/components/i18n/hooks'
import { Translation } from '@/components/i18n/Translation'
import { useCart } from '@/contexts/cart/cart-context'
import { useSearchParamsByKey } from '@/hooks/searchParams'

import { getGroupedDataList, parseInStock, recalculatePriceWithCover } from '../helpers'
import { OrderSummary } from '../OrderSummary'
import { usePolicyContext } from '../Policy.context'

import PolicyProductGroup from './ProductGroup'

export const ProductGroupGrid: FC<{ data: Policy; showSummary?: boolean }> = ({
  data,
  showSummary = true,
}) => {
  const translate = useTranslation()
  const policyId = usePolicyId()
  const [querySearch, setSearchQuery] = useSearchParamsByKey('search')
  const [searchFilter, setSearchFilter] = useState(querySearch || '')
  const debouncedAssetSearchTerm = useDebounce(searchFilter, 500)

  const policyCtx = usePolicyContext()
  const [selectedProductIds] = policyCtx.selectedProductIds
  const [selectedAccessoriesIds] = policyCtx.selectedAccessoriesIds
  const [, setActiveStep] = policyCtx.activeStep
  const { products, productGroups, isRelationsIgnored: relationsIgnored } = data
  const isRelationsIgnored = Boolean(relationsIgnored)
  const isOnlyProductView = Boolean(data.isLocked && data.isProductViewAllowedWhenLocked)

  const groupNames = productGroups.map(group => group.name)
  const { groupedProducts } = getGroupedDataList({
    priceList: products,
    groupNames,
    selectedProductIds,
    isRelationsIgnored,
    selectedAccessoriesIds,
    searchQuery: debouncedAssetSearchTerm,
  })
  const { getItemsByPolicyInOrder, hasGreenpoolItem } = useCart()
  const { allocation, coverLeft } = recalculatePriceWithCover({
    coverAvailable: data ? data.coverAmount - data.coverUsed : 0,
    priceList: products || [],
    selectedProductIds:
      data && !isOnlyProductView && !hasGreenpoolItem(policyId)
        ? getItemsByPolicyInOrder(data.id)
            .filter(item => !item.isGreenpoolProduct)
            .map(item => Number(item.productId))
        : [],
  })

  const isAccessoriesVisible = products.some(product => product.isAccessory && !product.isMandatory)
  const isNoProducts =
    products.filter(product => !product.isAccessory && !product.isMandatory).length === 0

  useEffect(() => {
    if (searchFilter === debouncedAssetSearchTerm) {
      setSearchFilter(querySearch || '')
    }
  }, [querySearch])

  useEffect(() => {
    setSearchQuery(debouncedAssetSearchTerm)
  }, [debouncedAssetSearchTerm, setSearchQuery])

  useEffect(() => {
    if (isAccessoriesVisible && isNoProducts) setActiveStep('ACCESSORIES')
  }, [isNoProducts, isAccessoriesVisible, setActiveStep])

  return (
    <>
      {(Object.values(groupedProducts).length > 9 ||
        Boolean(searchFilter || debouncedAssetSearchTerm)) && (
        <Input
          id="search"
          type="text"
          value={searchFilter}
          className="mt-2 py-2 text-base md:py-3"
          placeholder={translate('Type to filter')}
          onChange={e => setSearchFilter(e.target.value)}
        />
      )}

      {showSummary && <OrderSummary isFloating isAccessoriesVisible={isAccessoriesVisible} />}

      {products.length === 0 && (
        <div className="flex h-full w-full items-center justify-center">
          <p className="text-center text-lg font-medium text-gray-500">
            <Translation id="No available products" />
          </p>
        </div>
      )}

      {products.length > 0 && (
        <ul className="mobile:grid-cols-[repeat(auto-fill,_minmax(15rem,_1fr))] mx-0 my-2 grid list-none grid-cols-[repeat(auto-fill,_minmax(10rem,_1fr))] gap-2 p-0 pb-20 sm:gap-4">
          {Object.values(groupedProducts)
            .sort((a, b) => a.groupKey.localeCompare(b.groupKey))
            .map(productGroup => {
              // Filter out mandatory products for display in the group
              const products = productGroup.groupedProducts.filter(prod => !prod.isMandatory)
              const isSingle = products.length === 1
              const singleGroup:
                | Omit<ProductGroupData, 'category' | 'groupKey' | 'isRecommended'>
                | undefined = isSingle
                ? {
                    groupedProducts: products,
                    imageUrl: products[0].imgUrl,
                    priceFrom: products[0].price,
                    groupName: products[0].alias || products[0].name,
                    description: products[0].aliasDescription || products[0].description,
                    inStock: parseInStock(products[0].inStock),
                    isAccessory: Boolean(products[0].isAccessory),
                    isMandatory: Boolean(products[0].isMandatory),
                    assetType: products[0].assetType,
                  }
                : undefined

              return (
                <PolicyProductGroup
                  key={productGroup.groupKey}
                  {...(isSingle && singleGroup ? singleGroup : productGroup)}
                  groupedProducts={products}
                  allocation={allocation}
                  coverLeft={coverLeft}
                  isSelectionDisabled={isOnlyProductView || hasGreenpoolItem(policyId)}
                  searchQuery={debouncedAssetSearchTerm || ''}
                  isProductPriceVisible={data.isProductPriceVisible}
                />
              )
            })}
        </ul>
      )}
    </>
  )
}
